import { Form, Formik } from "formik";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Client } from "@/features/clients/types";
import { FormEditorActions } from "@/features/forms/components/form-editor-actions";
import type { Form8379Data, FormSubmission } from "@/features/forms/types";
import { IsaPart1 } from "./isa-part1";
import { IsaPart2 } from "./isa-part2";
import { IsaPart3 } from "./isa-part3";

const ALLOCATION_ROWS = [
  "inc_rep",
  "all_oth_inc",
  "adj_inc",
  "std_ded",
  "num_exmp",
  "credit",
  "oth_tax",
  "fed_tax",
  "payment",
] as const;
const ALLOCATION_COLS = ["jnt_ret", "inj_sp", "oth_sp"] as const;

const ALLOCATION_KEYS = ALLOCATION_ROWS.flatMap((row) =>
  ALLOCATION_COLS.map((col) => `${row}_${col}` as keyof Form8379Data),
);

const YN_KEYS: (keyof Form8379Data)[] = [
  "file_joint_ret",
  "joint_overpayment",
  "legally_oblig_pay",
  "resd_comm_prop_chk",
  "make_report_pay",
  "earned_income",
  "claim_earn_inc",
  "claim_refund_tax",
  "sp_ref_add_diff",
];
const TEXT_KEYS: (keyof Form8379Data)[] = [
  "tax_year",
  "res_comm_prop",
  "sp_name1",
  "sp_ssn1",
  "sp_name2",
  "sp_ssn2",
  "sp_diff_add",
];
const CHECKBOX_KEYS: (keyof Form8379Data)[] = [
  "sp_injure_chk",
  "sp_injure_chk2",
  "ref_both_name",
  "self_emp",
];

function toInitialValues(data: Record<string, unknown>): Form8379Data {
  const d = data as Partial<Form8379Data>;
  const out = {} as Form8379Data;
  for (const k of [...TEXT_KEYS, ...YN_KEYS, ...ALLOCATION_KEYS]) {
    (out[k] as string) = (d[k] as string) ?? "";
  }
  for (const k of CHECKBOX_KEYS) {
    (out[k] as boolean) = Boolean(d[k]);
  }
  return out;
}

const ssnDigits = (v: string | undefined) => (v ?? "").replace(/\D/g, "").length;

// Client-side UX validation for the Part III cells (the server re-validates:
// numeric ≥ 0, exemptions whole). Exemption rows are integer-only.
const MONEY_PATTERN = /^\d*\.?\d*$/;
const INTEGER_PATTERN = /^\d*$/;
const allocationRules = Object.fromEntries(
  ALLOCATION_KEYS.map((key) => [
    key,
    key.startsWith("num_exmp")
      ? Yup.string().matches(INTEGER_PATTERN, "Whole number only.")
      : Yup.string().matches(MONEY_PATTERN, "Enter a valid amount."),
  ]),
);

const schema = Yup.object({
  tax_year: Yup.string().max(50),
  sp_ssn1: Yup.string().test("ssn1", "Enter a 9-digit SSN.", (v) => {
    const n = ssnDigits(v);
    return n === 0 || n === 9;
  }),
  sp_ssn2: Yup.string().test("ssn2", "Enter a 9-digit SSN.", (v) => {
    const n = ssnDigits(v);
    return n === 0 || n === 9;
  }),
  res_comm_prop: Yup.string().when("resd_comm_prop_chk", {
    is: "Y",
    then: (s) => s.required("Enter the community-property state."),
  }),
  sp_diff_add: Yup.string().when("sp_ref_add_diff", {
    is: "Y",
    then: (s) => s.required("Enter the different refund address."),
  }),
  ...allocationRules,
});

type TabKey = "part1" | "part2" | "part3";
const PART_TO_TAB: Record<string, TabKey> = { "1": "part1", "2": "part2", "3": "part3" };
const TAB_TO_PART: Record<TabKey, string> = { part1: "1", part2: "2", part3: "3" };

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * The Innocent Spouse Analysis wizard: a 3-tab stepper (Part I / II / III) over
 * the client's single Form 8379 submission. One Formik form holds the whole
 * `data` blob so tab switches never lose data, and `FormEditorActions` provides
 * autosave + Print (preview) / Archive (save-to-Documents) + the e-sign stub.
 * The wizard and the plain 8379 editor are two views of the same record.
 */
export function Form8379Wizard({ client, submission, onSubmissionChange }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = PART_TO_TAB[searchParams.get("part") ?? "1"] ?? "part1";

  const goToTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    params.set("part", TAB_TO_PART[next]);
    setSearchParams(params, { replace: true });
  };

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Tabs value={tab} onValueChange={(v) => goToTab(v as TabKey)}>
          <TabsList>
            <TabsTrigger value="part1">Part I — Eligibility</TabsTrigger>
            <TabsTrigger value="part2">Part II — Injured Spouse</TabsTrigger>
            <TabsTrigger value="part3">Part III — Allocation</TabsTrigger>
          </TabsList>
          <TabsContent value="part1" className="mt-6">
            <IsaPart1 onNext={() => goToTab("part2")} />
          </TabsContent>
          <TabsContent value="part2" className="mt-6">
            <IsaPart2 client={client} onNext={() => goToTab("part3")} />
          </TabsContent>
          <TabsContent value="part3" className="mt-6">
            <IsaPart3 />
          </TabsContent>
        </Tabs>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
